// Step 16 — Contract drafts panel.
//
// Groups contracts into: DRAFTING / PENDING REVIEW / APPROVED /
// SENT FOR SIGNATURE / SIGNED / REJECTED. AI drafts show a clear
// "AI-utkast" indicator; approved/sent/signed rows have visible
// state that a human cannot confuse with automation.

import { useCallback, useEffect, useMemo, useState } from "react";

import { listContracts } from "./api";
import ContractDetail from "./ContractDetail";

export const STATUS_LABEL = {
  drafting: "Utkast",
  pending_review: "Under granskning",
  approved: "Godkänd",
  sent_for_signature: "Skickad för signering",
  signed: "Signerad",
  rejected: "Avvisad",
  cancelled: "Avbruten",
  superseded: "Ersatt",
};

export const KIND_LABEL = {
  nda: "NDA",
  loi: "LOI",
  spa: "SPA",
  apa: "APA",
  dd_agreement: "DD-avtal",
  management_agreement: "Ledningsavtal",
  advisory_agreement: "Rådgivaravtal",
  other: "Annat",
};

export const SOURCE_LABEL = {
  ai: "AI-utkast",
  human: "Manuellt",
};


export default function ContractsPanel({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listContracts(workspaceId, { limit: 200 });
      setItems(res?.results || []);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda kontrakt.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const g = {
      drafting: [], pending_review: [], approved: [],
      sent_for_signature: [], signed: [], terminal: [],
    };
    for (const c of items) {
      if (c.status === "drafting") g.drafting.push(c);
      else if (c.status === "pending_review") g.pending_review.push(c);
      else if (c.status === "approved") g.approved.push(c);
      else if (c.status === "sent_for_signature") g.sent_for_signature.push(c);
      else if (c.status === "signed") g.signed.push(c);
      else g.terminal.push(c);
    }
    return g;
  }, [items]);

  if (!workspaceId) return null;
  if (loading && items.length === 0) {
    return (
      <div className="qai-ct-panel">
        <div className="qai-ct-header">Kontrakt</div>
        <div className="qai-ct-loading">Laddar…</div>
      </div>
    );
  }

  return (
    <div className="qai-ct-panel">
      <div className="qai-ct-header">
        <span>Kontrakt</span>
        <span className="qai-ct-header-total">{items.length}</span>
      </div>
      {error && <div className="qai-ct-error">{error}</div>}

      <Section title="Utkast" items={groups.drafting} empty="Inga aktiva utkast.">
        {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
      </Section>
      <Section title="Under granskning" items={groups.pending_review} empty="Inget under granskning.">
        {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
      </Section>
      <Section title="Godkända — redo att skickas" items={groups.approved} empty="Inga godkända kontrakt.">
        {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
      </Section>
      <Section title="Skickade för signering" items={groups.sent_for_signature} empty="Inget skickat för signering.">
        {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
      </Section>
      <Section title="Signerade" items={groups.signed} empty="Inga signerade kontrakt än.">
        {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
      </Section>
      {groups.terminal.length > 0 && (
        <Section title="Avslutade / avvisade / ersatta" items={groups.terminal} empty="">
          {(c) => <Row c={c} onOpen={() => setSelectedId(c.id)} />}
        </Section>
      )}

      {selectedId && (
        <ContractDetail
          workspaceId={workspaceId}
          contractId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}

      <div className="qai-ct-disclaimer">
        Sigge kan ENDAST skapa utkast. Godkännande, signering och
        utskick är strikt manuella steg. Ingenting utförs
        automatiskt utanför denna vy.
      </div>
    </div>
  );
}


function Section({ title, items, empty, children }) {
  if (!items.length && !empty) return null;
  return (
    <section className="qai-ct-section">
      <h4>{title} <span className="qai-ct-count">{items.length}</span></h4>
      {items.length === 0 ? (
        empty ? <div className="qai-ct-empty">{empty}</div> : null
      ) : (
        <ul className="qai-ct-list">
          {items.map((c) => <li key={c.id}>{children(c)}</li>)}
        </ul>
      )}
    </section>
  );
}


function Row({ c, onOpen }) {
  return (
    <button type="button" className="qai-ct-row" onClick={onOpen}>
      <div className="qai-ct-row-title">
        {c.title}
        {c.source === "ai" && (
          <span className="qai-ct-tag qai-ct-tag-ai">AI-utkast</span>
        )}
      </div>
      <div className="qai-ct-row-meta">
        <span className="qai-ct-tag qai-ct-tag-kind">
          {KIND_LABEL[c.kind] || c.kind}
        </span>
        <span className={`qai-ct-tag qai-ct-tag-status qai-ct-tag-status-${c.status}`}>
          {STATUS_LABEL[c.status] || c.status}
        </span>
        {c.acquirer_name && (
          <span className="qai-ct-tag qai-ct-tag-buyer">{c.acquirer_name}</span>
        )}
        {c.uncertainties_count > 0 && (
          <span className="qai-ct-tag qai-ct-tag-warn">
            {c.uncertainties_count} osäkerhet
            {c.uncertainties_count === 1 ? "" : "er"}
          </span>
        )}
      </div>
    </button>
  );
}
